import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { Building2, Mic, Target, Layers, Truck, Cpu, Eye, FileText, ArrowRight } from 'lucide-react';

/**
 * Public capability map. It describes product workflows and validation status
 * without mapping confidential patent claims or filing scope.
 */

export const ENGINES = [
  {
    num: '01',
    slug: 'company-recognition',
    icon: Building2,
    name: { en: 'Company Recognition Engine', tr: 'Şirket Tanıma Motoru' },
    tag: { en: 'Capability Profiling', tr: 'Yetenek Profilleme' },
    desc: {
      en: 'Builds an organizational capability score per client — mapping industry, equipment inventory, talent density and supply-chain readiness into a vector used by every downstream engine.',
      tr: 'Her müşteri için organizasyonel yetenek skoru üretir — sektör, ekipman envanteri, yetenek yoğunluğu ve tedarik zinciri hazırlığını aşağı akış motorlarının kullanacağı vektöre dönüştürür.',
    },
    kpi: 'Capability vector profiling — validation in progress',
    status: 'Validation',
  },
  {
    num: '02',
    slug: 'voice-design',
    icon: Mic,
    name: { en: 'Voice-Commanded Design Engine', tr: 'Sesli Tasarım Motoru' },
    tag: { en: 'Speech → Spec', tr: 'Konuşma → Spesifikasyon' },
    desc: {
      en: 'A speech-to-specification prototype converts spoken briefs into structured fields. Language coverage and extraction accuracy vary and require user review.',
      tr: 'Konuşmadan spesifikasyona prototipi, sözlü briefleri yapılandırılmış alanlara dönüştürür. Dil kapsamı ve çıkarım doğruluğu değişebilir; kullanıcı incelemesi gerekir.',
    },
    kpi: 'Prototype · user review required',
    status: 'Prototype',
  },
  {
    num: '03',
    slug: 'multi-dim-optimization',
    icon: Target,
    name: { en: 'Multi-Dimensional Optimization Engine', tr: 'Çok-Boyutlu Optimizasyon Motoru' },
    tag: { en: '5-Axis Pareto', tr: '5-Eksenli Pareto' },
    desc: {
      en: 'Compares preliminary alternatives across cost, time, quality, innovation and risk. A verified Pareto solver remains roadmap work.',
      tr: 'Ön alternatifleri maliyet, süre, kalite, inovasyon ve risk açısından karşılaştırır. Doğrulanmış Pareto çözücü yol haritasındadır.',
    },
    kpi: 'AI-assisted comparison · solver roadmap',
    status: 'Roadmap',
  },
  {
    num: '04',
    slug: 'parametric-generative',
    icon: Layers,
    name: { en: 'Parametric-Generative Hybrid Engine', tr: 'Parametrik-Generatif Hibrit Motor' },
    tag: { en: 'Constraints + Diffusion', tr: 'Kısıt + Difüzyon' },
    desc: {
      en: 'Constraint-based parametric CAD rules fused with diffusion-model variations. Generated concepts are evaluated against available constraints; dimensional accuracy and manufacturability require independent engineering validation.',
      tr: 'Kısıt-tabanlı parametrik CAD kuralları, difüzyon model varyasyonlarıyla birleşir. Üretilen konseptler mevcut kısıtlara göre değerlendirilir; boyutsal doğruluk ve üretilebilirlik bağımsız mühendislik doğrulaması gerektirir.',
    },
    kpi: 'Preliminary outputs · dimensional validation required',
    status: 'Preliminary',
  },
  {
    num: '05',
    slug: 'supply-chain',
    icon: Truck,
    name: { en: 'Real-Time Supply Chain Engine', tr: 'Gerçek-Zamanlı Tedarik Zinciri Motoru' },
    tag: { en: 'Supplier Pricing — Roadmap', tr: 'Tedarikçi Fiyatları — Yol Haritası' },
    desc: {
      en: 'Supplier-pricing integration roadmap (raw materials, off-the-shelf components, fabrication lead-times); availability and data freshness depend on configured data sources.',
      tr: 'Tedarikçi fiyat entegrasyonu yol haritası (ham madde, hazır bileşenler, üretim lead-time); kullanılabilirlik ve veri güncelliği yapılandırılmış veri kaynaklarına bağlıdır.',
    },
    kpi: 'Supplier-pricing roadmap · configured data sources',
    status: 'Roadmap',
  },
  {
    num: '06',
    slug: 'system-orchestrator',
    icon: Cpu,
    name: { en: 'System Orchestrator', tr: 'Sistem Orkestratörü' },
    tag: { en: 'Application Orchestration', tr: 'Uygulama Orkestrasyonu' },
    desc: {
      en: 'Coordinates modules and asynchronous jobs inside the current application. Distributed-service isolation and SLA evidence remain deployment-specific roadmap work.',
      tr: 'Mevcut uygulama içindeki modülleri ve asenkron işleri koordine eder. Dağıtık servis izolasyonu ve SLA kanıtı, deployment bazlı yol haritası çalışmasıdır.',
    },
    kpi: 'Application orchestration · validation pending',
    status: 'Beta',
  },
  {
    num: '07',
    slug: 'vision-ar',
    icon: Eye,
    name: { en: 'Vision & AR Engine', tr: 'Görüntü & AR Motoru' },
    tag: { en: 'Vision Model + AR Draft', tr: 'Görsel Model + AR Taslağı' },
    desc: {
      en: 'Beta camera-to-observation workflow for field maintenance; equipment, fault and overlay suggestions require benchmarks and qualified review.',
      tr: 'Saha bakımı için beta kamera-gözlem iş akışı; ekipman, arıza ve overlay önerileri benchmark ve nitelikli inceleme gerektirir.',
    },
    kpi: 'Mobile/tablet AR · beta',
    status: 'Beta',
  },
  {
    num: '08',
    slug: 'documentation-synthesis',
    icon: FileText,
    name: { en: 'Documentation Synthesis Engine', tr: 'Dokümantasyon Sentez Motoru' },
    tag: { en: 'BOM · Cost · Work Order', tr: 'BOM · Maliyet · İş Emri' },
    desc: {
      en: 'Produces preliminary BOM, cost and manufacturing-oriented documentation. ERP/PLM ingestion depends on schema mapping, validation and a configured integration.',
      tr: 'Ön BOM, maliyet ve üretim-yönelimli dokümantasyon üretir. ERP/PLM aktarımı şema eşlemesi, doğrulama ve yapılandırılmış entegrasyona bağlıdır.',
    },
    kpi: 'Preliminary documents · integration-specific',
    status: 'Beta',
  },
];

export default function AIEnginesSection() {
  const { language } = useLanguage();
  const isTurkish = language === 'tr';

  return (
    <section id="engines" className="relative py-24 md:py-32 bg-[#060710] overflow-hidden" data-testid="ai-engines-section">
      {/* Ambient glows */}
      <div className="absolute inset-0 pointer-events-none opacity-50">
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] rounded-full bg-violet-500/10 blur-[150px]" />
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] rounded-full bg-cyan-500/10 blur-[140px]" />
      </div>
      <div className="pointer-events-none absolute top-20 right-0 w-[500px] h-[500px] bg-[#4F9DFF] opacity-[0.05] blur-[140px] rounded-full" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="mb-16 flex flex-wrap items-end justify-between gap-6"
        >
          <div className="max-w-2xl">
            <span className="inline-block px-3 py-1.5 border border-white/10 text-xs font-mono uppercase tracking-wider text-white/60 mb-6">
              {isTurkish ? 'Doğrulama Mimarisi' : 'Validation Architecture'}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white leading-[1.1] mb-4">
              {isTurkish ? (
                <>İzlenebilir inceleme kapıları, <span className="font-bold">tek kanıt izi</span></>
              ) : (
                <>Traceable review gates, <span className="font-bold">one evidence trail</span></>
              )}
            </h2>
            <p className="text-white/60 max-w-xl leading-relaxed">
              {isTurkish
                ? 'Bu kartlar mevcut ürün iş akışlarını ve doğrulama durumlarını gösterir. Her iş akışının prototip, beta veya roadmap sınırı teknik sayfada açıkça belirtilir; gizli IP kapsamı kamuya açık değildir.'
                : 'These cards show current product workflows and validation status. Prototype, beta and roadmap boundaries are stated on the technical page; confidential IP scope is not public.'}
            </p>
          </div>
          <Link
            to="/engines"
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/20 text-white/80 hover:text-white hover:bg-white/5 hover:border-white/30 transition-colors text-sm"
            data-testid="engines-deep-dive-link"
          >
            {isTurkish ? 'Teknik Dokümanı İncele' : 'View Technical Deep-Dive'}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
          {ENGINES.map((e, i) => {
            const Icon = e.icon;
            return (
              <motion.div
                key={e.num}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: i * 0.04 }}
                className="group relative bg-[#0A0D1A] p-6 hover:bg-[#0A0A0A] transition-colors flex flex-col"
                data-testid={`engine-${e.slug}`}
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="w-9 h-9 border border-white/20 flex items-center justify-center group-hover:border-white/40 transition-colors">
                    <Icon className="w-4 h-4 text-white/80" />
                  </div>
                  <span className="text-[10px] font-mono text-white/30 tracking-widest">{e.num}</span>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2">{e.tag[language] || e.tag.en}</div>
                <h3 className="text-sm font-medium text-white mb-2 leading-tight">{e.name[language] || e.name.en}</h3>
                <p className="text-xs text-white/55 leading-relaxed flex-1 mb-4">{e.desc[language] || e.desc.en}</p>
                <div className="pt-3 border-t border-white/10 flex items-center justify-between gap-2">
                  <span className="text-[10px] font-mono text-white/50 truncate">↗ {e.kpi}</span>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-white/30 flex-shrink-0">{e.status}</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
